import { useCallback, useMemo, useState } from "react";

import { Tooltip } from "antd";

import { usePreferedAgentVMDropsData } from "ServerData/PrerecordedVoiceMessage/Hooks/usePrerecordedVoiceMessageData/usePrerecordedVoiceMessageData";
import { useFlags } from "Services/FeatureFlagService";

import { BUTTONS_MESSAGES } from "../../CallBar.consts";
import { CallBarButton } from "../CallBarButton";
import { VoicemailSettingsButton } from "./Components/VoicemailSettingsButton/VoicemailSettingsButton";

import styles from "./VoicemailDropButton.module.scss";

export interface VoicemailDropButtonProps {
    dropActive: boolean;
    campaignId?: string;
    voicemailDropDisabled: boolean;
    dropVoicemail: (id: string) => Promise<void>;
    contactPhone: string;
    profileId?: string;
}

const AGENT_CALLBAR_LOCATION = "AgentCallbarQuery";
const VOICEMAIL_SETTINGS_TOOLTIP = "Change Voicemail Selection";
const NO_VOICEMAILS_AVAILABLE = "No Voicemails Available";
const NO_OTHER_VOICEMAILS_AVAILABLE = "No Other Voicemails";
const NO_SELECTION_MADE = "Please select a voicemail first";

export function VoicemailDropButton({
    campaignId,
    dropVoicemail,
    voicemailDropDisabled = false,
    dropActive,
    contactPhone,
    profileId,
}: VoicemailDropButtonProps): JSX.Element {
    const { useEnhancedVoicemailDrop } = useFlags();
    if (useEnhancedVoicemailDrop) {
        return (
            <VoicemailDropButtonEnhanced
                dropVoicemail={dropVoicemail}
                voicemailDropDisabled={voicemailDropDisabled}
                dropActive={dropActive}
                contactPhone={contactPhone}
                profileId={profileId}
                campaignId={campaignId}
            />
        );
    }
    return (
        <VoicemailDropButtonLegacy
            dropVoicemail={dropVoicemail}
            voicemailDropDisabled={voicemailDropDisabled}
            dropActive={dropActive}
            contactPhone={contactPhone}
            profileId={profileId}
            campaignId={campaignId}
        />
    );
}

function VoicemailDropButtonLegacy({
    dropVoicemail,
    voicemailDropDisabled = false,
    dropActive,
    contactPhone,
    profileId,
    campaignId,
}: VoicemailDropButtonProps): JSX.Element {
    const { hasPreRecordedVmDrops } = useFlags();
    const { loading, data } = usePreferedAgentVMDropsData({
        contactPhone,
        campaignId,
        profileId,
        invokedLocation: AGENT_CALLBAR_LOCATION,
    });

    const defaultVoicemailDrop = data?.defaultRecording || data?.list?.[0];

    const [vmRecordingId, setVmRecordingId] = useState<string>("");
    const [open, setOpen] = useState(false);

    const toggleModal = useCallback(() => {
        if (!voicemailDropDisabled) {
            setOpen(!open);
        }
    }, [open, voicemailDropDisabled]);

    const handleSelectedVoicemailDrop = useCallback(async () => {
        try {
            await dropVoicemail(vmRecordingId);
            setOpen(false);
        } catch (error) {
            return Promise.reject(error);
        }
    }, [dropVoicemail, vmRecordingId]);

    // we are using a special endpoint, and [0] is always the default to use
    const handleDefaultVoicemailDrop = useCallback(async () => {
        await dropVoicemail(defaultVoicemailDrop?.id);
    }, [defaultVoicemailDrop, dropVoicemail]);

    const VMMessages = useMemo(() => {
        return {
            ...BUTTONS_MESSAGES.voicemailDrop,
            tooltip: () => `${defaultVoicemailDrop?.friendlyName}`,
        };
    }, [defaultVoicemailDrop]);

    const noDefaultVoicemails = !data || data?.list.length === 0;
    // Because if there is no selected default voicemail, we take the first voicemail as default if there is one
    const noVoicemails = !data || data?.list.length < 2;

    const voicemailDropOptions = useMemo(
        () =>
            defaultVoicemailDrop
                ? [defaultVoicemailDrop, ...data?.list.filter((v) => v.id !== defaultVoicemailDrop.id)]
                : data?.list,
        [data, defaultVoicemailDrop]
    );

    const voicemailButton = (
        <Tooltip title={noDefaultVoicemails ? NO_VOICEMAILS_AVAILABLE : undefined} placement="top">
            <div>
                <CallBarButton
                    id="voicemailDrop"
                    active={dropActive}
                    action={handleDefaultVoicemailDrop}
                    icon="voicemail"
                    disabled={voicemailDropDisabled || noDefaultVoicemails}
                    messages={VMMessages}
                />
            </div>
        </Tooltip>
    );

    if (!hasPreRecordedVmDrops) {
        return voicemailButton;
    }

    return (
        <div className={styles.container}>
            <VoicemailSettingsButton
                voicemailDropDisabled={voicemailDropDisabled}
                open={open}
                campaignId={campaignId}
                onVoicemailDrop={handleSelectedVoicemailDrop}
                voiceMailOptionData={voicemailDropOptions}
                loading={loading}
                dropActive={dropActive}
                setVmRecordingId={setVmRecordingId}
                toggleModal={toggleModal}
                noVoicemails={noVoicemails}
                tooltipMessage={noVoicemails ? NO_OTHER_VOICEMAILS_AVAILABLE : VOICEMAIL_SETTINGS_TOOLTIP}
            />
            {voicemailButton}
        </div>
    );
}

function VoicemailDropButtonEnhanced({
    dropVoicemail,
    voicemailDropDisabled = false,
    dropActive,
    contactPhone,
    profileId,
    campaignId,
}: VoicemailDropButtonProps): JSX.Element {
    const { hasPreRecordedVmDrops } = useFlags();
    const { loading, data } = usePreferedAgentVMDropsData({
        contactPhone,
        campaignId,
        profileId,
        invokedLocation: AGENT_CALLBAR_LOCATION,
    });

    const defaultVoicemailDrop = data?.defaultRecording;
    const hasDefaultRecording = !!defaultVoicemailDrop;

    const [vmRecordingId, setVmRecordingId] = useState<string>("");
    const [open, setOpen] = useState(false);

    // Check if there are no voicemails available at all
    const noVoicemailsAvailable = !data || data?.list.length === 0;

    // Check if there's no default recording and no selection made
    const noSelectionMade = !hasDefaultRecording && !vmRecordingId;

    const toggleModal = useCallback(() => {
        // Disable modal toggle when no voicemails available or when explicitly disabled
        if (!voicemailDropDisabled && !noVoicemailsAvailable) {
            setOpen(!open);
        }
    }, [open, voicemailDropDisabled, noVoicemailsAvailable]);

    const handleSelectedVoicemailDrop = useCallback(async () => {
        try {
            await dropVoicemail(vmRecordingId);
            setOpen(false);
        } catch (error) {
            return Promise.reject(error);
        }
    }, [dropVoicemail, vmRecordingId]);

    // Use default recording if available, otherwise use selected recording
    const handleVoicemailDrop = useCallback(async () => {
        if (hasDefaultRecording && defaultVoicemailDrop) {
            await dropVoicemail(defaultVoicemailDrop.id);
        } else if (vmRecordingId) {
            await dropVoicemail(vmRecordingId);
        }
    }, [hasDefaultRecording, defaultVoicemailDrop, dropVoicemail, vmRecordingId]);

    const VMMessages = useMemo(() => {
        return {
            ...BUTTONS_MESSAGES.voicemailDrop,
            tooltip: () => {
                if (noVoicemailsAvailable) {
                    return NO_VOICEMAILS_AVAILABLE;
                } else if (hasDefaultRecording) {
                    return `${defaultVoicemailDrop?.friendlyName}`;
                } else if (vmRecordingId) {
                    return "Drop selected voicemail";
                } else {
                    return NO_SELECTION_MADE;
                }
            },
        };
    }, [noVoicemailsAvailable, hasDefaultRecording, defaultVoicemailDrop, vmRecordingId]);

    // Only show "No Other Voicemails" when there's a default but no other options
    const noOtherVoicemails = hasDefaultRecording && data?.list.length < 2;

    // Disable button when no voicemails available, no default recording and no selection made, or explicitly disabled
    const isButtonDisabled = voicemailDropDisabled || noVoicemailsAvailable || noSelectionMade;

    const voicemailDropOptions = useMemo(
        () =>
            defaultVoicemailDrop
                ? [defaultVoicemailDrop, ...data?.list.filter((v) => v.id !== defaultVoicemailDrop.id)]
                : data?.list,
        [data, defaultVoicemailDrop]
    );

    const voicemailButton = (
        <Tooltip title={noVoicemailsAvailable ? NO_VOICEMAILS_AVAILABLE : undefined} placement="top">
            <div>
                <CallBarButton
                    id="voicemailDrop"
                    active={dropActive}
                    action={handleVoicemailDrop}
                    icon="voicemail"
                    disabled={isButtonDisabled}
                    messages={VMMessages}
                />
            </div>
        </Tooltip>
    );

    if (!hasPreRecordedVmDrops) {
        return voicemailButton;
    }

    return (
        <div className={styles.container}>
            <VoicemailSettingsButton
                voicemailDropDisabled={voicemailDropDisabled || noVoicemailsAvailable}
                open={open}
                campaignId={campaignId}
                onVoicemailDrop={handleSelectedVoicemailDrop}
                voiceMailOptionData={voicemailDropOptions}
                loading={loading}
                dropActive={dropActive}
                setVmRecordingId={setVmRecordingId}
                toggleModal={toggleModal}
                noVoicemails={noOtherVoicemails}
                tooltipMessage={
                    noVoicemailsAvailable
                        ? NO_VOICEMAILS_AVAILABLE
                        : noOtherVoicemails
                          ? NO_OTHER_VOICEMAILS_AVAILABLE
                          : VOICEMAIL_SETTINGS_TOOLTIP
                }
            />
            {voicemailButton}
        </div>
    );
}
